
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService, Roles } from '../services/auth.service';
import { ProximityRightEnum } from '@models';

/** Redirect to the appropriate home page base on user role */
export const homeGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router: Router = inject(Router);

  const user = await authService.getLoggedInUser();
  if (user) {
    if (!user.email || !user.emailConfirmed) {
      return router.createUrlTree(['/auth/send-confirm-email']);
    }

    // All Account-tier admins (PAA + AccountAdmin + ApprovalAdmin + OrderAdmin +
    // SupportAdmin) and GlobalAdmins land on the account-admin home tile page. That
    // page shows only the tiles the user's rights allow, so restricted variants
    // without CanApprove don't get stranded on a page that requires a right they lack.
    // CanEditUserByApprovalTree is common to every Account-tier role, and GlobalAdmin's
    // hasRight() returns true for everything.
    if (user.hasRight(ProximityRightEnum.CanEditUserByApprovalTree)) {
      // GlobalAdmin has no accountId; passing null/undefined into createUrlTree
      // serializes to the literal string "null" (…/home/null). Drop the segment.
      const homeSegments = user?.accountId
        ? ['/account-admin/home', user.accountId]
        : ['/account-admin/home'];
      return router.createUrlTree(homeSegments);
    }
    else if (user.hasRight(ProximityRightEnum.CanManageAccountAdministration, ProximityRightEnum.CanManageAccountOrganization, ProximityRightEnum.CanEditProgram)) {
      return router.createUrlTree(['/account/accounts']);
    }
    else if (user.hasRight(ProximityRightEnum.CanCreateJobOrders)) {
      return router.createUrlTree(['/account-user/home', user?.accountId]);
    }
    else if (user.hasRight(ProximityRightEnum.CanManageSupplierAdministration, ProximityRightEnum.CanManageSupplierPartnerships, ProximityRightEnum.CanManageSupplierProducts)) {
      return router.createUrlTree(['/supplier/suppliers', user?.supplierId, 'manage']);
    }
    // shouldn't get here - every logged-in user should have one of the above roles
    console.log('User does not have required rights');
  }
  // no user or no rights - go to login page
  const redirectUrl = authService.getDestUrl();
  return router.createUrlTree(['/login'], { queryParams: { redirectUrl } });
}
